import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { dciAPI, cceAPI } from '../services/api';
import './ListPage.css';
import './Dashboard.css';

export default function Dashboard() {
  const { user } = useAuth();

  const [global, setGlobal]     = useState(null);
  const [members, setMembers]   = useState([]);
  const [cycles, setCycles]     = useState([]);
  const [cceMeta, setCceMeta]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');

  useEffect(() => {
    if (!user || user.role !== 'admin') return;

    let cancelled = false;
    setLoading(true);
    setError('');

    Promise.all([
      dciAPI.adminList(),
      cceAPI.list(),
    ])
      .then(([dciRes, cceRes]) => {
        if (cancelled) return;
        setGlobal(dciRes.data.meta);
        setMembers(dciRes.data.data || []);
        setCycles(cceRes.data.data || []);
        setCceMeta(cceRes.data.meta || null);
      })
      .catch(() => { if (!cancelled) setError('Impossible de charger les données du tableau de bord.'); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [user]);

  // ── Garde d'accès — réservé aux admins ───────────────────────────────
  if (!user) {
    return (
      <div className="list-page">
        <div className="empty">Vous devez être connecté pour accéder à cette page.</div>
      </div>
    );
  }
  if (user.role !== 'admin') {
    return (
      <div className="list-page">
        <div className="empty">Accès réservé à l'administration de YesNeDni.</div>
      </div>
    );
  }

  // Trie les contributeurs CCE par score DCI décroissant (score vient de members, même source que la section DCI)
  const rankedContributors = cycles
    .map((c) => ({ ...c, dciScore: members.find((m) => m.user_id === c.contributor_id)?.score ?? 0 }))
    .sort((a, b) => b.dciScore - a.dciScore || b.total_contributions - a.total_contributions);

  return (
    <div className="list-page dashboard-page">
      <div className="page-header">
        <h1>Tableau de bord</h1>
        <p><strong>Démonstration —</strong>{' '}Ces indicateurs illustrent le fonctionnement du modèle sur un jeu de données de démonstration. Les agrégats sont calculés en temps réel ; les visualisations individuelles sont anonymisées et aucune donnée personnelle n'est affichée.</p>
      </div>

      {loading && <div className="loading">Chargement des données...</div>}
      {error && <div className="empty">{error}</div>}

      {!loading && !error && (
        <>
          {/* ── Diaspora Contribution Index ─────────────────────────── */}
          <section className="dash-section">
            <h2 className="dash-section-title">Diaspora Contribution Index™</h2>
            <p className="dash-section-sub">
              Indice composite 0–1000 mesurant la contribution des membres diaspora selon trois
              dimensions : formation transmise (40 %), savoir partagé (35 %) et opportunités générées (25 %).
            </p>

            <div className="dash-stats">
              <div className="dash-stat-card">
                <span className="dash-stat-value">{global?.indice_global ?? '–'}</span>
                <span className="dash-stat-label">Indice global (sur 1000)</span>
              </div>
              <div className="dash-stat-card">
                <span className="dash-stat-value">{global?.taux_participation ?? '–'}%</span>
                <span className="dash-stat-label">Taux de participation</span>
              </div>
              <div className="dash-stat-card">
                <span className="dash-stat-value">{global?.membres_actifs ?? '–'} / {global?.membres_count ?? '–'}</span>
                <span className="dash-stat-label">Membres actifs / total</span>
              </div>
            </div>

            {members.length === 0 ? (
              <p className="dash-empty">Aucun membre diaspora enregistré pour l'instant.</p>
            ) : (
              <div className="dash-table-wrap">
                <table className="dash-table">
                  <thead>
                    <tr>
                      <th>Membre</th>
                      <th>Wilaya</th>
                      <th>Score DCI</th>
                      <th>Formation</th>
                      <th>Partage</th>
                      <th>Opportunités</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((m) => (
                      <tr key={m.user_id}>
                        <td className="dash-member-name">{m.name}</td>
                        <td>{m.wilaya || '–'}</td>
                        <td><span className="dash-score-badge">{m.score} <span className="dash-denom">/1000</span></span></td>
                        <td>{m.dimensions.formation.valeur} {m.dimensions.formation.unite} <span className="dash-points">({m.dimensions.formation.points} pts)</span></td>
                        <td>{m.dimensions.partage.valeur} {m.dimensions.partage.unite} <span className="dash-points">({m.dimensions.partage.points} pts)</span></td>
                        <td>{m.dimensions.opportunites.valeur} {m.dimensions.opportunites.unite} <span className="dash-points">({m.dimensions.opportunites.points} pts)</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* ── Contribution Cycle Engine ───────────────────────────── */}
          <section className="dash-section">
            <h2 className="dash-section-title">Contribution Cycle Engine™</h2>
            <p className="dash-section-sub">
              Chaînes de transmission détectées dans l'écosystème — qui a contribué, et qui en a bénéficié.
              {cceMeta?.has_cycles && ' Des cycles de réciprocité ont été identifiés (un bénéficiaire devenu contributeur).'}
            </p>

            {cycles.length === 0 ? (
              <p className="dash-empty">{cceMeta?.note || 'Aucune contribution enregistrée pour l\'instant.'}</p>
            ) : (
              <div className="dash-table-wrap">
                <table className="dash-table">
                  <thead>
                    <tr>
                      <th>Contributeur</th>
                      <th>Wilaya</th>
                      <th>Score DCI</th>
                      <th>Contributions CCE</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rankedContributors.map((c) => (
                      <tr key={c.contributor_id}>
                        <td className="dash-member-name">
                          {c.name}
                          <span className="dash-role-tag">{c.role}</span>
                        </td>
                        <td>{c.wilaya || '–'}</td>
                        <td><span className="dash-score-badge">{c.dciScore} <span className="dash-denom">/1000</span></span></td>
                        <td>{c.total_contributions}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
